/**
 * How It Works Page
 */

import React from 'react';
import { CheckCircle, ArrowDown, Heart } from 'lucide-react';

const STEPS = [
  {
    number: '01',
    icon: '📝',
    title: 'سجّل عند خالتي هيلة',
    description: 'أنشئ حسابك بإيميلك، وخالتي هيلة راح تسألك أسئلة عشان تتعرف عليك وعلى اللي تبحث عنه',
    details: [
      'تسجيل سريع بالإيميل',
      'أسئلة عن شخصيتك وقيمك',
      'تفضيلاتك في الشريك',
      'كل بياناتك مشفرة ومحمية'
    ],
    color: 'rose'
  },
  {
    number: '02',
    icon: '🤖',
    title: 'خالتي تحلل وتدوّر',
    description: 'الذكاء الاصطناعي يحلل إجاباتك ويبحث في قاعدة البيانات عن أفضل شخص يناسبك',
    details: [
      'تحليل ذكي للشخصية',
      'مطابقة القيم والاهتمامات',
      'التوافق في نمط الحياة',
      'نسبة توافق دقيقة'
    ],
    color: 'purple'
  },
  {
    number: '03',
    icon: '💕',
    title: 'تعرّف على توافقك',
    description: 'خالتي هيلة ترشح لك شخص واحد في كل مرة - لأن الجد يبي تركيز',
    details: [
      'بروفايل مفصّل',
      'نسبة التوافق',
      'نقاط مشتركة',
      'قرار قبول أو رفض'
    ],
    color: 'pink'
  },
  {
    number: '04',
    icon: '💬',
    title: 'سولفوا 7 أيام',
    description: 'إذا قبلتوا بعض، تبدأ فترة تعارف 7 أيام تتعرفون على بعض أكثر',
    details: [
      '7 أيام كافية للتعارف',
      'اقتراحات مواضيع من AI',
      'تمديد حتى 4 مرات إذا احتجتوا',
      'انسحاب محترم بأي وقت'
    ],
    color: 'blue'
  },
  {
    number: '05',
    icon: '📱',
    title: 'تبادلوا التواصل',
    description: 'إذا حسيتوا إن فيه نصيب وتوافق، شاركوا معلومات التواصل وأكملوا بالحلال',
    details: [
      'مشاركة رقم الجوال',
      'مشاركة الانستقرام',
      'أو تبادل داخل المحادثة',
      'قرار بيدكم 100%'
    ],
    color: 'green'
  }
];

const FAQ_PREVIEW = [
  {
    q: 'وش يصير لو ما عجبني التوافق؟',
    a: 'ترفض وتنتظر 48 ساعة وبعدها خالتي هيلة تجيب لك توافق جديد'
  },
  {
    q: 'أقدر أنسحب من المحادثة؟',
    a: 'طبعاً! بأي وقت، وخالتي هيلة ترسل رسالة لطيفة للطرف الثاني'
  },
  {
    q: 'هل المنصة مجانية؟',
    a: 'نعم! حالياً مجانية بالكامل'
  }
];

export default function HowItWorks() {
  const getColorClasses = (color) => {
    const colors = {
      rose: 'from-rose-500 to-rose-600',
      purple: 'from-purple-500 to-purple-600',
      pink: 'from-pink-500 to-pink-600',
      blue: 'from-blue-500 to-blue-600',
      green: 'from-green-500 to-green-600',
    };
    return colors[color] || colors.rose;
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-purple-50 via-rose-50 to-pink-50 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="text-5xl mb-4">👩‍🦳🔍</div>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            كيف تشتغل خالتي هيلة؟
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            خمس خطوات بسيطة من التسجيل إلى لقاء نصيبك بالحلال
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          {STEPS.map((step, index) => (
            <div key={index} className="relative">
              {/* Connection Line */}
              {index < STEPS.length - 1 && (
                <div className="absolute right-8 top-32 bottom-0 w-0.5 bg-gradient-to-b from-gray-200 to-transparent hidden md:block" />
              )}
              
              <div className="flex flex-col md:flex-row gap-6 mb-12 md:mb-16">
                {/* Number & Icon */}
                <div className="flex md:flex-col items-center gap-4 md:gap-2">
                  <div className={`w-16 h-16 bg-gradient-to-br ${getColorClasses(step.color)} rounded-2xl flex items-center justify-center text-3xl shadow-lg`}>
                    {step.icon}
                  </div>
                  <span className="text-4xl font-bold text-gray-200">{step.number}</span>
                </div>
                
                {/* Content */}
                <div className="flex-1 bg-gradient-to-br from-gray-50 to-rose-50/30 rounded-2xl p-6 md:p-8">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {step.description}
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Arrow */}
              {index < STEPS.length - 1 && (
                <div className="flex justify-center mb-8 md:hidden">
                  <ArrowDown className="w-6 h-6 text-rose-300" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Quick FAQ */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-rose-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              أسئلة سريعة
            </h2>
          </div>

          <div className="space-y-4">
            {FAQ_PREVIEW.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-rose-500 via-red-500 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="text-5xl mb-4">👩‍🦳💕</div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            جاهز تبدأ؟
          </h2>
          <p className="text-lg text-white/90 mb-8">
            خالتي هيلة تنتظرك عشان تلاقي لك نصيبك
          </p>
          <a
            href="#/app"
            className="inline-flex items-center gap-2 px-10 py-4 text-lg font-bold text-rose-600 bg-white rounded-full hover:shadow-xl hover:scale-105 transition-all"
          >
            يلا نبدأ
            <Heart className="w-5 h-5 fill-rose-500 text-rose-500" />
          </a>
        </div>
      </section>
    </div>
  );
}
